/**
 * AdminAuthGuard.tsx — Route guard for admin-protected pages
 *
 * Purpose:
 *   Renders protected child routes only when the user is authenticated with
 *   an admin-role JWT. Redirects unauthenticated or non-admin users to the
 *   /login page via React Router v6 <Navigate>.
 *
 * Public API:
 *   - AdminAuthGuard  Route-guard component (use as a layout route)
 *
 * Architecture Context:
 *   HITL resolution: role check = claims.role === 'admin' (string, AT-50.01).
 *   Reads auth state from AdminAuthContext via useAdminAuth().
 *   Used as <Route element={<AdminAuthGuard />}> in App.tsx.
 *
 * @module packages/billing-admin/src/guards/AdminAuthGuard
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';

/**
 * AdminAuthGuard — layout-route guard component.
 *
 * Redirects to /login if:
 *   1. No JWT token is present (unauthenticated)
 *   2. JWT token is present but claims.role !== 'admin' (insufficient role)
 *
 * ARCHITECTURE: Using <Outlet> as the success path integrates with react-
 * router-dom v6 nested routes. The parent route defines the guard element;
 * child routes are rendered via Outlet when access is granted.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export default function AdminAuthGuard(): React.JSX.Element {
  const { user, token } = useAdminAuth();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  // Redirect if no token
  if (!token) {
    return <Navigate to="/login" replace state={{ returnTo, autoStart: true }} />;
  }

  // Redirect if token present but role is not 'admin'
  // DECISION (AT-50.01): role check is a string equality per HITL resolution
  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" replace state={{ returnTo, autoStart: false }} />;
  }

  // Authenticated and authorised — render child routes
  return <Outlet />;
}
